import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search, Filter, Calendar, ChevronRight } from 'lucide-react';

import apiService from '../utils/api';
import logo from '../asset/logo.jpg'
const News = () => {

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsResponse, categoriesResponse] = await Promise.all([
          apiService.getBlogPosts({ category: selectedCategory }),
          apiService.getCategories('blog')
        ]);

        setPosts(postsResponse.data.posts);
        setCategories(categoriesResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
     <Helmet>
  <title>Travel News & Updates — YS Sky Travel and Tour Agency P.L.C</title>
  <meta name="description" content="Latest travel news, updates, promotions and announcements." />
</Helmet>

<div className="absolute top-0 left-0 w-full h-32 pointer-events-none 
                bg-gradient-to-b from-white/80 to-transparent 
                dark:from-black/80 z-10"></div>

      <section className="relative h-72 bg-gradient-to-r from-primary-900 via-primary-600 to-medical-600 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={logo}
            alt="Blog"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/70 to-medical-600/50"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <motion.div 
            className="text-center w-full"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          News and Blog
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
           get updates & tips from YS Sky Travel & Tour Agency 
            </p>
          </motion.div>
        </div>

        {/* Animated Elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full animate-float"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white/10 rounded-full animate-float" style={{ animationDelay: '2s' }}></div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4 lg:px-0">
          <div className="flex flex-col lg:flex-row gap-10">
            
            {/* Main Content */}
            <div className="flex-1 lg:order-1">
              {/* Search & Filter */}
              <div className="flex flex-col md:flex-row items-center gap-4 mb-8">
                <div className="relative flex-1 w-full">
                  <input
                    type="text"
                    placeholder="Search news..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-700 rounded-lg py-2 pl-10 pr-4 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="text-gray-500" size={18} />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="border border-gray-300 dark:border-gray-700 rounded-lg py-2 px-3 focus:ring-2 focus:ring-primary-500 focus:outline-none transition"
                  >
                    <option value="">All Categories</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.name}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Blog Posts */}
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-primary-500"></div>
                </div>
              ) : filteredPosts.length > 0 ? (
                <div className="grid gap-8 md:grid-cols-2">
                  {filteredPosts.map(post => (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5 }}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden flex flex-col md:flex-row"
                    >
                      <div className="md:w-1/3">
                        <img
                          src={`https://backend.ysskytravel.com${post.image_url}`}
                          alt={post.title}
                          className="w-full h-48 md:h-full object-cover"
                        />
                      </div>
                      <div className="p-5 md:w-2/3 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                            <Calendar size={16} className="mr-2" />
                            {new Date(post.publish_date).toLocaleDateString()}
                            {post.category && (
                              <>
                                <span className="mx-2">•</span>
                                <span>{post.category}</span>
                              </>
                            )}
                          </div>
                          <h2 className="text-lg md:text-xl font-semibold mb-2 text-gray-900 dark:text-white line-clamp-2">{post.title}</h2>
                          <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">{post.excerpt}</p>
                        </div>
                        <Link
                          to={`/news/${post.id}`}
                          className="mt-auto inline-flex items-center text-primary-500 hover:text-primary-600 font-medium transition"
                        >
                          Read More <ChevronRight size={16} className="ml-1" />
                        </Link>
                      </div>
                    </motion.article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">No News Available</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:w-80 flex-shrink-0 lg:order-2">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Recent Posts</h3>
                <div className="space-y-4">
                  {posts.slice(0, 5).map(post => (
                    <Link
                      key={post.id}
                      to={`/news/${post.id}`}
                      className="block group"
                    >
                      <p className="text-gray-900 dark:text-white group-hover:text-primary-500 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
                        {post.title}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(post.publish_date).toLocaleDateString()}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      selectedCategory === '' ? 'bg-primary-500 text-white border-primary-500' : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
                    }`}
                  >
                    All
                  </button>
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.name)}
                      className={`px-3 py-1 rounded-full text-sm border ${
                        selectedCategory === category.name ? 'bg-primary-500 text-white border-primary-500' : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;
